import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MediaPost } from '@/lib/media';
import { likePost, incrementPostView, formatCount, isYoutubeUrl } from '@/lib/media';
import YoutubeEmbed from './YoutubeEmbed';
import VerifiedBadge from './VerifiedBadge';
import TagModal from './TagModal';

interface VideoFeedProps {
  videos: MediaPost[];
  certifiedArtists: Set<string>;
}

function VideoFeed({ videos, certifiedArtists }: VideoFeedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [liked, setLiked] = useState<Set<string>>(new Set());
  const [likesCount, setLikesCount] = useState<Record<string, number>>({});
  const [viewsCount, setViewsCount] = useState<Record<string, number>>({});
  const [muted, setMuted] = useState(true);
  const countedRef = useRef<Set<string>>(new Set());
  const [tagTarget, setTagTarget] = useState<MediaPost | null>(null);
  const [taggedMap, setTaggedMap] = useState<Record<string, string[]>>({});
  const [tagToast, setTagToast] = useState('');

  useEffect(() => {
    const videos = containerRef.current?.querySelectorAll('video');
    if (!videos) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target as HTMLVideoElement;
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    videos.forEach((v) => observer.observe(v));
    return () => observer.disconnect();
  }, [videos]);

  const handleLike = (video: MediaPost) => {
    const isLiked = liked.has(video.id);
    const next = new Set(liked);
    if (isLiked) {
      next.delete(video.id);
    } else {
      next.add(video.id);
    }
    setLiked(next);

    const base = likesCount[video.id] ?? video.likes;
    setLikesCount((prev) => ({
      ...prev,
      [video.id]: isLiked ? base - 1 : base + 1,
    }));

    if (!isLiked && !(video as MediaPost & { demo?: boolean }).demo) {
      likePost(video.id).catch(() => {});
    }
  };

  const handlePlay = (video: MediaPost) => {
    if ((video as MediaPost & { demo?: boolean }).demo) return;
    if (countedRef.current.has(video.id)) return;
    countedRef.current.add(video.id);
    setViewsCount((prev) => ({
      ...prev,
      [video.id]: (prev[video.id] ?? video.views) + 1,
    }));
    incrementPostView(video.id).catch(() => {});
  };

  const handleShare = async (video: MediaPost) => {
    const shareData = {
      title: video.title,
      text: `${video.title} — ${video.artist_name || ''}`,
      url: window.location.href,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        /* annulé */
      }
    } else {
      navigator.clipboard?.writeText(window.location.href).catch(() => {});
    }
  };

  const handleTag = (handles: string[]) => {
    if (!tagTarget) return;
    setTaggedMap((prev) => ({
      ...prev,
      [tagTarget.id]: Array.from(new Set([...(prev[tagTarget.id] || []), ...handles])),
    }));
    setTagToast(`${handles.join(', ')} a/ont été mentionné(s) sur « ${tagTarget.title} »`);
    setTagTarget(null);
    window.setTimeout(() => setTagToast(''), 3200);
  };

  if (videos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mb-4">
          <i className="ri-video-line text-3xl text-foreground-400" />
        </div>
        <p className="text-foreground-400">Aucune vidéo dans cette catégorie pour le moment.</p>
      </div>
    );
  }

  return (
    <>
      <div ref={containerRef} className="flex flex-col gap-6">
      {videos.map((video) => {
        const isLiked = liked.has(video.id);
        const currentLikes = likesCount[video.id] ?? video.likes;
        const currentViews = viewsCount[video.id] ?? video.views;

        if (isYoutubeUrl(video.video_url)) {
          return (
            <div
              key={video.id}
              className="rounded-xl overflow-hidden border border-background-200/70 bg-background-50"
            >
              <div className="relative aspect-video bg-background-950">
                <YoutubeEmbed
                  url={video.video_url}
                  title={video.title}
                  className="absolute inset-0 w-full h-full"
                />
                <span className="absolute top-3 left-3 inline-block px-3 py-1 rounded-full bg-background-950/50 backdrop-blur-sm text-xs font-semibold text-foreground-50 pointer-events-none">
                  {video.category}
                </span>
              </div>
              <div className="p-4 md:p-5">
                <h3 className="font-heading font-semibold text-base md:text-lg text-foreground-50 leading-snug mb-1">
                  {video.title}
                </h3>
                {video.description && (
                  <p className="text-sm text-foreground-400 leading-relaxed mb-3 line-clamp-2">
                    {video.description}
                  </p>
                )}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    {video.artist_name && (
                      <Link
                        to={`/media/artiste/${encodeURIComponent(video.artist_name)}`}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground-50 hover:text-primary-400 transition-colors"
                      >
                        <i className="ri-user-star-line text-accent-400" />
                        {video.artist_name}
                        {certifiedArtists.has(video.artist_name) && <VerifiedBadge />}
                      </Link>
                    )}
                    {video.location && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-foreground-400">
                        <i className="ri-map-pin-line text-accent-400" />
                        {video.location}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-xs text-foreground-400">
                      <i className="ri-eye-line" />
                      {formatCount(video.views)} vues
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleLike(video)}
                      className="inline-flex items-center gap-1.5 text-xs text-foreground-400 hover:text-accent-400 transition-colors"
                      aria-label="Aimer"
                    >
                      <i
                        className={`text-lg ${
                          isLiked ? 'ri-heart-3-fill text-accent-400' : 'ri-heart-3-line'
                        }`}
                      />
                      {formatCount(currentLikes)}
                    </button>
                    <button
                      onClick={() => handleShare(video)}
                      className="inline-flex items-center gap-1.5 text-xs text-foreground-400 hover:text-accent-400 transition-colors"
                      aria-label="Partager"
                    >
                      <i className="ri-share-forward-line text-lg" />
                      Partager
                    </button>
                    <button
                      onClick={() => setTagTarget(video)}
                      className="inline-flex items-center gap-1.5 text-xs text-foreground-400 hover:text-accent-400 transition-colors"
                      aria-label="Mentionner"
                    >
                      <i className="ri-at-line text-lg" />
                      Mentionner
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div
            key={video.id}
            className="relative w-full h-[68vh] md:h-[75vh] rounded-xl overflow-hidden bg-background-950"
          >
            {/* Vidéo */}
            <video
              src={video.video_url}
              poster={video.thumbnail_url || undefined}
              muted={muted}
              loop
              playsInline
              preload="metadata"
              onPlay={() => handlePlay(video)}
              className="absolute inset-0 w-full h-full object-cover"
            />

            {/* Dégradé bas */}
            <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none" />

            {/* Badge catégorie */}
            <div className="absolute top-4 left-4">
              <span className="inline-block px-3 py-1 rounded-full bg-background-950/50 backdrop-blur-sm text-xs font-semibold text-foreground-50">
                {video.category}
              </span>
            </div>

            {/* Bouton muet */}
            <button
              onClick={() => setMuted(!muted)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-background-950/50 backdrop-blur-sm text-foreground-50 hover:bg-background-950/70 transition-colors"
              aria-label={muted ? 'Activer le son' : 'Couper le son'}
            >
              <i className={`ri-${muted ? 'volume-mute' : 'volume-up'}-line text-lg`} />
            </button>

            {/* Rail d'actions à droite */}
            <div className="absolute right-4 bottom-24 flex flex-col items-center gap-5">
              <button
                onClick={() => handleLike(video)}
                className="flex flex-col items-center gap-1 text-foreground-50 transition-transform hover:scale-110"
                aria-label="Aimer"
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-background-950/40 backdrop-blur-sm">
                  <i
                    className={`text-xl ${
                      isLiked ? 'ri-heart-3-fill text-accent-400' : 'ri-heart-3-line'
                    }`}
                  />
                </div>
                <span className="text-xs font-medium">{formatCount(currentLikes)}</span>
              </button>
              <button
                onClick={() => handleShare(video)}
                className="flex flex-col items-center gap-1 text-foreground-50 transition-transform hover:scale-110"
                aria-label="Partager"
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-background-950/40 backdrop-blur-sm">
                  <i className="ri-share-forward-line text-xl" />
                </div>
                <span className="text-xs font-medium">Partager</span>
              </button>
              <button
                onClick={() => setTagTarget(video)}
                className="flex flex-col items-center gap-1 text-foreground-50 transition-transform hover:scale-110"
                aria-label="Mentionner"
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-background-950/40 backdrop-blur-sm">
                  <i className="ri-at-line text-xl" />
                </div>
                <span className="text-xs font-medium">Mentionner</span>
              </button>
            </div>

            {/* Infos bas */}
            <div className="absolute left-4 right-20 bottom-4">
              <h3 className="font-heading font-semibold text-lg text-foreground-50 leading-snug mb-1">
                {video.title}
              </h3>
              {video.description && (
                <p className="text-sm text-foreground-300 leading-relaxed mb-2 line-clamp-2">
                  {video.description}
                </p>
              )}
              <div className="flex items-center gap-3 flex-wrap">
                {video.artist_name && (
                  <Link
                    to={`/media/artiste/${encodeURIComponent(video.artist_name)}`}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground-50 hover:text-primary-400 transition-colors"
                  >
                    <i className="ri-user-star-line text-accent-400" />
                    {video.artist_name}
                    {certifiedArtists.has(video.artist_name) && <VerifiedBadge />}
                  </Link>
                )}
                {video.location && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-foreground-300">
                    <i className="ri-map-pin-line text-accent-400" />
                    {video.location}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 text-xs text-foreground-300">
                  <i className="ri-eye-line" />
                  {formatCount(currentViews)} vues
                </span>
              </div>
              {(taggedMap[video.id] || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {taggedMap[video.id].map((h) => (
                    <span
                      key={h}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background-950/50 backdrop-blur-sm text-foreground-50 text-[11px]"
                    >
                      <i className="ri-at-line text-accent-400" />
                      {h}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>

      {tagToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-full bg-foreground-950 text-foreground-50 text-sm font-medium flex items-center gap-2">
          <i className="ri-at-line text-accent-400" />
          {tagToast}
        </div>
      )}

      <TagModal
        open={!!tagTarget}
        title={tagTarget?.title || ''}
        onClose={() => setTagTarget(null)}
        onTag={handleTag}
      />
    </>
  );
}

export default VideoFeed;